import { Inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import {
  AppPartnerSettingsService,
  Currency,
  Frequency,
  MarketplacePackagesApiService,
  PartnerMarketContainer,
  Price,
  Pricing,
  RetailPrice,
} from '@vendasta/marketplace-packages';
import { DisplayPriceService } from '@vendasta/shared';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { AppPartnerService, EditionChange, EditionUpgradeAction, UpgradeDialogContent } from '@galaxy/marketplace-apps';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { AppSettings } from '@vendasta/marketplace-apps';
import { AGIDTOKEN, IS_IMPERSONATING_TOKEN } from '../local-seo';
import { DomainService } from '@vendasta/domain';
import { AccountGroupApiService, GetMultiRequest, ProjectionFilter } from '@vendasta/account-group';

interface RedirectInfo {
  url: string;
  isImpersonation: boolean;
}

export interface PartnerMarket {
  marketId: string;
  partnerId: string;
}

export const appID = 'MS';

@Injectable({
  providedIn: 'root',
})
export class UpgradeCTADialogService {
  vbcHost$: Observable<string>;
  partnerMarket$: Observable<PartnerMarket>;
  public appSettings$: Observable<AppSettings>;

  constructor(
    @Inject(AGIDTOKEN) private agid$: Observable<string>,
    @Inject(IS_IMPERSONATING_TOKEN) private isImpersonation$: Observable<boolean>,
    private translateService: TranslateService,
    private matSnackBar: MatSnackBar,
    private snowplowService: ProductAnalyticsService,
    private appPartnerService: AppPartnerService,
    private appPartnerSettingsService: AppPartnerSettingsService,
    private marketplacePackagesService: MarketplacePackagesApiService,
    private displayPriceService: DisplayPriceService,
    private domainService: DomainService,
    private accountGroupService: AccountGroupApiService,
  ) {
    this.partnerMarket$ = this.agid$.pipe(
      switchMap((businessID) =>
        businessID
          ? this.accountGroupService
              .getMulti(
                new GetMultiRequest({
                  accountGroupIds: [businessID],
                  projectionFilter: new ProjectionFilter({
                    accountGroupExternalIdentifiers: true,
                  }),
                }),
              )
              .pipe(
                map((resp) => {
                  let partnerId = resp.accountGroups[0]?.accountGroup?.accountGroupExternalIdentifiers?.partnerId;
                  const marketId = resp.accountGroups[0]?.accountGroup?.accountGroupExternalIdentifiers?.marketId;
                  if (partnerId.toUpperCase() === 'DW1') {
                    partnerId = 'DW';
                  }
                  if (partnerId.toUpperCase() === 'VEND') {
                    partnerId = 'SRP';
                  }
                  return {
                    partnerId: partnerId,
                    marketId: marketId,
                  };
                }),
                filter((val) => !!val),
              )
          : of(null),
      ),
      distinctUntilChanged(),
      shareReplay(1),
    );
    const defaultAppSettings = new AppSettings({
      editionChange: {
        editionUpgradeAction: EditionUpgradeAction.EDITION_UPGRADE_ACTION_CONTACT_SALES,
      },
      branding: { enabled: false },
    });

    this.appSettings$ = this.partnerMarket$.pipe(
      switchMap((pm) => {
        return this.appPartnerService.getAppSettings(appID, pm.partnerId, pm.marketId).pipe(
          catchError((err) => {
            if (err.status === 404) {
              // The selected market doesn't have auto-activation setting
              return of(defaultAppSettings);
            }
            return throwError(err);
          }),
          map((response: AppSettings) => {
            return response ? response : defaultAppSettings;
          }),
        );
      }),
      shareReplay(1),
    );

    this.vbcHost$ = this.partnerMarket$.pipe(
      switchMap((pm) =>
        this.domainService.getDomainByIdentifier(`/application/VBC/partner/${pm.partnerId}`).pipe(
          map((resp) => resp.primary.domain),
          shareReplay(1),
        ),
      ),
    );
  }

  getUpgradeText(retailPrice: RetailPrice, editionChangeDetails: EditionChange): string {
    if (!retailPrice) {
      return null;
    }

    if (retailPrice.price !== null && !(retailPrice.price < 0)) {
      // The price isn't `contact sales`.
      let upgradeSuffix = 'UPGRADE_NOW';
      if (
        !!editionChangeDetails &&
        editionChangeDetails.editionUpgradeAction === EditionUpgradeAction.EDITION_UPGRADE_ACTION_CUSTOM_ORDER &&
        !!editionChangeDetails.customUpgradePackageIds &&
        editionChangeDetails.customUpgradePackageIds.length > 1
      ) {
        upgradeSuffix = 'STARTING_AT';
      }
      return this.translateService.instant(`UPGRADE_DIALOG.UPGRADE_TEXT.${upgradeSuffix}`);
    }
    return null;
  }

  getFrequencyText(retailPrice: RetailPrice): string {
    if (!retailPrice) {
      return null;
    }

    if (!!retailPrice.price && retailPrice.price > 0) {
      const frequencyKey: string = Frequency[retailPrice.billingFrequency || Frequency.MONTHLY];
      return this.translateService.instant(`UPGRADE_DIALOG.FREQUENCY.${frequencyKey}`);
    }
    return null;
  }

  getEditionChangeDetails$(): Observable<EditionChange> {
    return this.appSettings$.pipe(
      map((response: AppSettings) => (response?.editionChange ? response.editionChange : new EditionChange())),
      shareReplay(1),
    );
  }

  getAddonUpgradeDetails$(addonID: string): Observable<EditionChange> {
    const defaultEditionChange = new EditionChange({
      editionUpgradeAction: EditionUpgradeAction.EDITION_UPGRADE_ACTION_CONTACT_SALES,
    });

    return this.partnerMarket$.pipe(
      switchMap((pm) => {
        return this.appPartnerService.getAppSettings(addonID, pm.partnerId, pm.marketId).pipe(
          catchError((err) => {
            if (err.status === 404) {
              // The selected market doesn't have auto-activation setting
              return of(defaultEditionChange);
            }
            return throwError(err);
          }),
          map((response: AppSettings) => {
            return response?.editionChange ? response.editionChange : defaultEditionChange;
          }),
        );
      }),
      shareReplay(1),
    );
  }

  getCustomUpgradeModalContent$(addonID?: string): Observable<UpgradeDialogContent> {
    return this.partnerMarket$.pipe(
      switchMap((pm) => {
        return this.appPartnerService.getUpgradeDialogContent(pm.partnerId, pm.marketId, addonID ? addonID : appID);
      }),
      catchError(() => of(null)),
      map((response) => (response ? response.upgradeDialogContent : null)),
    );
  }

  getFormattedRetailPrice$(retailPrice: RetailPrice): Observable<string> {
    if (!retailPrice) {
      // The price is hidden.
      return of('');
    }
    const currencyKey: string = Currency[retailPrice.currency || Currency.USD];
    return this.displayPriceService.formatDisplayPrice(retailPrice.price, currencyKey, null, true, true, true, false);
  }

  getUpgradePrice$(
    pmConfig: PartnerMarket,
    addonID: string,
    editionChangeDetails: EditionChange,
    editionID?: string,
  ): Observable<RetailPrice> {
    if (
      !editionChangeDetails?.editionUpgradeAction ||
      editionChangeDetails.editionUpgradeAction === EditionUpgradeAction.EDITION_UPGRADE_ACTION_CREATE_ORDER
    ) {
      return this.getRetailPriceForAddonID$(addonID, pmConfig, editionID);
    } else if (editionChangeDetails.editionUpgradeAction === EditionUpgradeAction.EDITION_UPGRADE_ACTION_CUSTOM_ORDER) {
      if (editionChangeDetails.priceReferencePackageId) {
        return this.getMostFrequentPriceForPackage$(editionChangeDetails.priceReferencePackageId);
      }
    }
    return of(null);
  }

  getRetailPriceForAddonID$(
    addonID: string,
    partnerMarket: PartnerMarket,
    editionId?: string,
  ): Observable<RetailPrice> {
    const pmConfig = new PartnerMarketContainer({
      partnerId: partnerMarket.partnerId,
      marketId: partnerMarket.marketId,
    });
    return combineLatest([
      this.appPartnerSettingsService
        .getRetailPrice(addonID, pmConfig, editionId, true)
        .pipe(catchError(() => of(null))),
      this.appPartnerSettingsService
        .getRetailPrice(addonID, pmConfig, editionId, false)
        .pipe(catchError(() => of(null))),
    ]).pipe(map(([overridePrice, defaultPrice]: [RetailPrice, RetailPrice]) => overridePrice || defaultPrice));
  }

  getMostFrequentPriceForPackage$(packageID: string): Observable<RetailPrice> {
    return this.marketplacePackagesService
      .getMultiPackages({
        packageIds: [packageID],
      })
      .pipe(
        map((response) => {
          if (!!response.packageContainers && response.packageContainers.length > 0) {
            const container = response.packageContainers[0];
            return findMostFrequentPrice(container.package.pricing);
          } else {
            return {
              price: -1,
            } as RetailPrice;
          }
        }),
      );
  }

  buildBusinessCenterUpgradeRedirectInfo(
    item: UpgradeRedirectItem,
    upgradeAction: EditionUpgradeAction,
  ): Observable<RedirectInfo> {
    return combineLatest([this.agid$, this.vbcHost$, this.isImpersonation$]).pipe(
      map(([agid, vbcHost, isImpersonation]) => {
        const nextURL = encodeURIComponent(window.location.href);
        const itemsJSON = encodeURIComponent(JSON.stringify([item]));
        const upgradePath =
          upgradeAction === EditionUpgradeAction.EDITION_UPGRADE_ACTION_CONTACT_SALES
            ? 'edition-upgrades'
            : 'orders/create/confirmation';
        const purchaseAddOnURL = `https://${vbcHost}/account/location/${agid}/${upgradePath}?items=${itemsJSON}&nextUrl=${nextURL}`;
        return <RedirectInfo>{
          url: purchaseAddOnURL,
          isImpersonation: isImpersonation,
        };
      }),
    );
  }

  sendToVbc(redirectInfo: RedirectInfo) {
    if (redirectInfo.isImpersonation) {
      this.matSnackBar.open(this.translateService.instant('SHARED.UPGRADE_MODAL.IMPERSONATION_FORBIDDEN'), null, {
        duration: 2000,
      });
    } else {
      window.location.href = redirectInfo.url;
    }
  }
}

function normalizeFrequencyEnum(value: keyof typeof Frequency | Frequency): Frequency {
  if (typeof value === 'string') {
    return Frequency[value];
  }

  return value;
}

export function findMostFrequentPrice(pricing: Pricing): RetailPrice {
  if (!pricing || !pricing.prices || pricing.prices.length <= 0) {
    return {
      price: -1,
    } as RetailPrice;
  }

  // Sort the prices by frequency.
  const mostFrequentPrice = pricing.prices.sort((a: Price, b: Price): number => {
    if (a.price === null || a.price < 0) {
      // The price is `contact sales`
      return 1;
    }

    const orderedFrequencies = [
      Frequency.DAILY,
      Frequency.WEEKLY,
      Frequency.MONTHLY,
      Frequency.YEARLY,
      Frequency.ONCE,
      Frequency.OTHER,
    ];
    a.frequency = normalizeFrequencyEnum(a.frequency || Frequency.MONTHLY);
    b.frequency = normalizeFrequencyEnum(b.frequency || Frequency.MONTHLY);
    const aIndex = orderedFrequencies.indexOf(a.frequency);
    const bIndex = orderedFrequencies.indexOf(b.frequency);
    return aIndex !== bIndex ? aIndex - bIndex : a.price - b.price;
  })[0]; // The first price is the most frequent.

  if (mostFrequentPrice.price === undefined) {
    mostFrequentPrice.price = 0;
  } else if (mostFrequentPrice.price === null) {
    mostFrequentPrice.price = -1;
  }

  return {
    price: mostFrequentPrice.price,
    billingFrequency: mostFrequentPrice.frequency,
    currency: pricing.currency,
  } as RetailPrice;
}

// Usage of this interface will differ based on upgrade type:
// - Edition: appId and editionId
// - Addon: appId and addonId
// - Package: packageId
export interface UpgradeRedirectItem {
  appId?: string;
  editionId?: string;
  addonId?: string;
  packageId?: string;
}
